import Phaser from 'phaser';
import { sceneAfter } from '../game/scenes';
import { addHeroFramesToTexture, HERO_IDLE_FRAME } from '../game/heroFrames';
import { attachKeyboard, createEmptyControls, type ControlState } from '../input/KeyboardAdapter';
import { createVirtualControls } from '../input/VirtualControls';
import { createIconHud } from '../ui/IconHud';

type LayerInfo = {
  name: string;
  color: number;
  top: number;
  bottom: number;
  description: string;
  drillMs: number;
};

const LAYERS: LayerInfo[] = [
  {
    name: 'Superficie',
    color: 0xb7b0a7,
    top: 220,
    bottom: 430,
    description: 'Aquí hay polvo, rocas pequeñas y huellas en la Luna.',
    drillMs: 2000,
  },
  {
    name: 'Corteza',
    color: 0x9398a4,
    top: 430,
    bottom: 760,
    description: 'La corteza es la capa dura que está por fuera.',
    drillMs: 3000,
  },
  {
    name: 'Manto',
    color: 0x6a5d68,
    top: 760,
    bottom: 1120,
    description: 'En el manto hay rocas pesadas y muy calientes.',
    drillMs: 4000,
  },
  {
    name: 'Manto profundo',
    color: 0x4d4049,
    top: 1120,
    bottom: 1500,
    description: 'Más abajo las rocas están apretadas y guardan calor.',
    drillMs: 5000,
  },
  {
    name: 'Núcleo',
    color: 0xd27d48,
    top: 1500,
    bottom: 1860,
    description: 'Este es el núcleo, la capa principal y más caliente.',
    drillMs: 6000,
  },
];

export class DrillScene extends Phaser.Scene {
  private controls!: ControlState;
  private player!: Phaser.GameObjects.Image;
  private drillBody!: Phaser.GameObjects.Image;
  private drillBit!: Phaser.GameObjects.Image;
  private hint!: Phaser.GameObjects.Text;
  private barBg!: Phaser.GameObjects.Rectangle;
  private barFill!: Phaser.GameObjects.Rectangle;
  private barLabel!: Phaser.GameObjects.Text;
  private dialogBg!: Phaser.GameObjects.Rectangle;
  private dialogTitle!: Phaser.GameObjects.Text;
  private dialogText!: Phaser.GameObjects.Text;
  private state: 'idle' | 'drilling' | 'paused' | 'ascending' | 'complete' = 'idle';
  private currentLayer = 0;
  private drillProgress = 0;
  private frameTick = 0;
  private frameIndex = 0;
  private vibrateOffset = 0;

  constructor() {
    super('Drill');
  }

  preload() {
    this.load.image('hero_sheet', 'assets/hero-sheet-v3.png');
    this.load.image('drill_body', 'assets/drill-body.svg');
    this.load.image('drill_bit', 'assets/drill-bit.svg');
  }

  create() {
    createIconHud(this);
    this.controls = createEmptyControls();
    attachKeyboard(this, this.controls);
    createVirtualControls(this, this.controls);

    addHeroFramesToTexture(this, 'hero_sheet');

    const worldHeight = LAYERS[LAYERS.length - 1].bottom + 260;
    const shaftX = this.scale.width * 0.5;

    this.physics.world.gravity.y = 0;
    this.cameras.main.setBounds(0, 0, this.scale.width, worldHeight);

    this.add.rectangle(0, 0, this.scale.width, worldHeight, 0x05070f).setOrigin(0);
    this.add.rectangle(this.scale.width / 2, 118, this.scale.width, 240, 0x111723);
    this.add.circle(this.scale.width * 0.78, 92, 48, 0xf1ebdf, 0.9);

    LAYERS.forEach((layer) => {
      const midY = (layer.top + layer.bottom) / 2;
      const layerHeight = layer.bottom - layer.top;
      this.add.rectangle(this.scale.width / 2, midY, this.scale.width, layerHeight, layer.color, 1);
      this.add.text(this.scale.width - 26, midY, layer.name, {
        fontFamily: 'monospace',
        fontSize: '16px',
        color: '#f7f7f7',
      }).setOrigin(1, 0.5);
    });

    this.add.rectangle(shaftX, worldHeight / 2, 210, worldHeight - 180, 0x202631, 0.82);
    this.add.rectangle(shaftX, worldHeight / 2, 124, worldHeight - 180, 0x0a0e15, 0.78);
    this.add.rectangle(shaftX, 174, 260, 24, 0x8891a2);
    this.add.text(shaftX, 132, 'Perfora hasta el núcleo', {
      fontFamily: 'monospace',
      fontSize: '22px',
      color: '#f3f7ff',
    }).setOrigin(0.5);

    this.player = this.add.image(shaftX - 24, 239, 'hero_sheet', HERO_IDLE_FRAME).setScale(0.6);
    this.drillBody = this.add.image(shaftX + 20, 182, 'drill_body').setScale(0.6).setOrigin(0.5);
    this.drillBit = this.add.image(shaftX + 20, 216, 'drill_bit').setScale(0.6).setOrigin(0.5);
    this.updateDrillVisual();

    // Progress bar (HUD)
    const barW = 200;
    const barX = this.scale.width / 2;
    const barY = 58;
    this.barBg = this.add.rectangle(barX, barY, barW, 18, 0x222838, 0.9)
      .setScrollFactor(0).setDepth(2000);
    this.barFill = this.add.rectangle(barX - barW / 2, barY, 0, 14, 0x4ae67a, 1)
      .setOrigin(0, 0.5).setScrollFactor(0).setDepth(2001);
    this.barLabel = this.add.text(barX, barY, '', {
      fontFamily: 'monospace',
      fontSize: '12px',
      color: '#ffffff',
    }).setOrigin(0.5).setScrollFactor(0).setDepth(2002);
    this.setBarVisible(false);

    // Layer dialog (appears near character when a layer is reached)
    const dw = Math.min(360, this.scale.width - 40);
    this.dialogBg = this.add.rectangle(0, 0, dw, 100, 0x0a0e18, 0.92)
      .setStrokeStyle(2, 0x8891a2)
      .setDepth(3000);
    this.dialogTitle = this.add.text(0, 0, '', {
      fontFamily: 'monospace',
      fontSize: '20px',
      fontStyle: 'bold',
      color: '#ffe5a3',
    }).setOrigin(0.5).setDepth(3001);
    this.dialogText = this.add.text(0, 0, '', {
      fontFamily: 'monospace',
      fontSize: '15px',
      color: '#dce8ff',
      wordWrap: { width: dw - 32 },
      align: 'center',
    }).setOrigin(0.5, 0).setDepth(3001);
    this.setDialogVisible(false);

    this.hint = this.add.text(20, 20, 'Mantén espacio para perforar la primera capa.', {
      fontFamily: 'monospace',
      fontSize: '18px',
      color: '#eef6ff',
      wordWrap: { width: Math.min(440, this.scale.width - 40) },
    }).setScrollFactor(0).setDepth(2000);

    this.cameras.main.startFollow(this.player, true, 0.08, 0.08);
  }

  update(_time: number, delta: number) {
    if (this.state === 'idle') {
      if (this.controls.jumpHeld) {
        this.state = 'drilling';
        this.drillProgress = 0;
        this.setBarVisible(true);
      }
      return;
    }

    if (this.state === 'paused') {
      if (this.controls.action) {
        this.setDialogVisible(false);
        this.currentLayer++;
        if (this.currentLayer >= LAYERS.length) {
          this.startAscent();
        } else {
          this.state = 'drilling';
          this.drillProgress = 0;
          this.setBarVisible(true);
          this.hint.setText(`Perforando ${LAYERS[this.currentLayer].name}...`);
        }
      }
      return;
    }

    if (this.state === 'drilling') {
      const layer = LAYERS[this.currentLayer];

      if (this.controls.jumpHeld) {
        this.drillProgress = Math.min(this.drillProgress + delta, layer.drillMs);

        // Move player proportionally through the layer
        const pct = this.drillProgress / layer.drillMs;
        this.player.y = layer.top + (layer.bottom - layer.top) * pct;

        // Update progress bar
        this.barFill.width = 196 * pct;
        this.barLabel.setText(`${layer.name} ${Math.floor(pct * 100)}%`);
        this.hint.setText(`Perforando ${layer.name}... ¡No sueltes!`);
      } else {
        this.hint.setText(`Mantén espacio para seguir perforando ${layer.name}.`);
      }

      // Animate character & drill vibration
      this.frameTick += delta;
      if (this.frameTick > 110 && this.controls.jumpHeld) {
        this.frameTick = 0;
        this.frameIndex = (this.frameIndex + 1) % 3;
        this.player.setTexture('hero_sheet', `hero_${this.frameIndex}`);
      }
      this.vibrateOffset = this.controls.jumpHeld ? Math.sin(Date.now() * 0.025) * 3 : 0;
      this.updateDrillVisual();

      // Layer complete
      if (this.drillProgress >= layer.drillMs) {
        this.state = 'paused';
        this.vibrateOffset = 0;
        this.player.setTexture('hero_sheet', HERO_IDLE_FRAME);
        this.updateDrillVisual();
        this.setBarVisible(false);

        this.showDialog(layer.name, layer.description);
        this.hint.setText('');

        if (this.currentLayer >= LAYERS.length - 1) {
          this.add.circle(this.player.x + 20, this.player.y + 20, 110, 0xffa45d, 0.18);
        }
        // all layers wait for space press (handled in 'paused' state)
      }
      return;
    }

    if (this.state === 'ascending') {
      this.frameTick += delta;
      if (this.frameTick > 110) {
        this.frameTick = 0;
        this.frameIndex = (this.frameIndex + 1) % 3;
        this.player.setTexture('hero_sheet', `hero_${this.frameIndex}`);
      }
      this.vibrateOffset = Math.sin(Date.now() * 0.025) * 3;
      this.updateDrillVisual();
    }
  }

  private setBarVisible(visible: boolean) {
    this.barBg.setVisible(visible);
    this.barFill.setVisible(visible);
    this.barLabel.setVisible(visible);
    if (visible) {
      this.barFill.width = 0;
      this.barLabel.setText('');
    }
  }

  private showDialog(title: string, body: string) {
    this.dialogTitle.setText(title);
    this.dialogText.setText(body);
    // Position near the player
    const dx = this.player.x + 120;
    const dy = this.player.y - 20;
    this.dialogBg.setPosition(dx, dy);
    this.dialogTitle.setPosition(dx, dy - 28);
    this.dialogText.setPosition(dx, dy - 6);
    // Resize bg to fit text
    const h = 60 + this.dialogText.height;
    this.dialogBg.setSize(this.dialogBg.width, h);
    this.setDialogVisible(true);
  }

  private setDialogVisible(visible: boolean) {
    this.dialogBg.setVisible(visible);
    this.dialogTitle.setVisible(visible);
    this.dialogText.setVisible(visible);
  }

  private startAscent() {
    this.state = 'ascending';
    this.setDialogVisible(false);
    this.hint.setText('Subiendo de vuelta a la superficie...');

    this.tweens.add({
      targets: this.player,
      y: 239,
      duration: 3000,
      ease: 'Sine.easeInOut',
      onUpdate: () => this.updateDrillVisual(),
      onComplete: () => {
        this.state = 'complete';
        this.vibrateOffset = 0;
        this.player.setTexture('hero_sheet', HERO_IDLE_FRAME);
        this.updateDrillVisual();
        this.hint.setText('Subiste de nuevo a la superficie. Misión cumplida.');
        this.time.delayedCall(900, () => {
          const next = sceneAfter('Drill');
          if (next) {
            this.scene.start(next);
          }
        });
      },
    });
  }

  private updateDrillVisual() {
    this.drillBody.x = this.player.x + 44;
    this.drillBody.y = this.player.y + 6;
    this.drillBit.x = this.player.x + 44;
    this.drillBit.y = this.player.y + 40 + this.vibrateOffset;
  }
}
